import React, { Suspense, lazy, useCallback, useEffect, useId, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { btnPrimaryClass, btnSecondaryClass, formControlClass } from "../../../dashboardStyles";
import ConfirmDialog from "../../../ConfirmDialog";
import { useDismissable } from "../../wizard/hooks/useDismissable";
import {
  ABOUT_CLASSES,
  ABOUT_MAX_LENGTH,
  aboutToHtml,
  normalizeAboutEmbeds,
  sanitizeAboutHtml,
  serializeAboutHtml,
} from "../../../../../utils/aboutContent";
import {
  BLOCK_TYPES,
  STRUCTURES,
  blockTitle,
  blockToHtml,
  countElements,
  createBlock,
  createStructure,
  formatAboutHtml,
  parseBlocks,
  serializeBlocks,
} from "../../../../../utils/aboutBlocks";
import BlockForm from "./BlockForms";

const HtmlCodeEditor = lazy(() => import("./HtmlCodeEditor"));

const k = "Dashboard.AboutEditor";

export const BLOCK_ICONS = {
  text: "bi-fonts",
  cards: "bi-grid-3x2-gap",
  media: "bi-image",
  faq: "bi-question-circle",
  link: "bi-link-45deg",
  booking: "bi-calendar-check",
  custom: "bi-code-slash",
};

const modeButtonClass = (active) =>
  `inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border px-4 text-sm font-semibold transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 disabled:cursor-not-allowed disabled:opacity-50 ${
    active ? "border-brand-300 bg-brand-50 text-brand-700" : "border-ink-200 bg-white text-ink-600 hover:border-ink-300 hover:text-ink-900"
  }`;

const toolButtonClass =
  "inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold text-ink-600 transition hover:bg-ink-100 hover:text-ink-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 disabled:cursor-not-allowed disabled:opacity-40";

const dangerToolClass =
  "inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold text-red-600 transition hover:bg-red-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 disabled:cursor-not-allowed disabled:opacity-40";

/** A button that opens a short menu of choices under itself. */
const MenuButton = ({ label, icon, primary = false, items, onPick, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismissable(open, close);
  const menuId = useId();

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
        className={primary ? btnPrimaryClass : btnSecondaryClass}
      >
        <i className={`bi ${icon}`} aria-hidden="true" />
        {label}
        <i className="bi bi-chevron-down text-xs" aria-hidden="true" />
      </button>
      {open && (
        <div
          id={menuId}
          role="menu"
          className="absolute top-full left-0 z-30 mt-2 w-72 max-w-[calc(100vw-2.5rem)] rounded-card border border-ink-200 bg-white p-1.5 shadow-card-hover"
        >
          {items.map((item) => (
            <button
              key={item.value}
              type="button"
              role="menuitem"
              onClick={() => {
                close();
                onPick(item.value);
              }}
              className="flex w-full cursor-pointer items-start gap-3 rounded-lg px-3 py-2.5 text-left transition hover:bg-ink-50 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-brand-600"
            >
              <i className={`bi ${item.icon} mt-0.5 text-base text-brand-600`} aria-hidden="true" />
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-ink-900">{item.label}</span>
                {item.description && <span className="block text-xs text-ink-500">{item.description}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

MenuButton.propTypes = {
  label: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  primary: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string, description: PropTypes.string, icon: PropTypes.string }),
  ).isRequired,
  onPick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

/**
 * The About section's editor, in two modes over one stored value.
 *
 * Visual: the section as blocks (utils/aboutBlocks.js), one edited at a time —
 * picked from the list or by a click on it in the preview — moved, removed,
 * added from a menu, or a whole page started from a structure. Every edit is
 * written straight back as HTML, so the preview follows the typing.
 *
 * HTML: the same content as code. Edits there are a draft until "Apply HTML":
 * half-typed markup is not something to preview or save, so the page keeps the
 * last applied version, and saving waits until the draft is applied or
 * dropped (`onStatusChange` tells the page why).
 */
const AboutBlocksEditor = ({
  value = "",
  onChange,
  disabled = false,
  onBusyChange,
  selected = null,
  onSelect,
  onStatusChange,
}) => {
  const { t } = useTranslation();
  const ids = useId();
  const [blocks, setBlocks] = useState(() => parseBlocks(value));
  const lastEmitted = useRef(value);
  const [mode, setMode] = useState("visual");
  const [tooLong, setTooLong] = useState(false);
  const [imageBusy, setImageBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [draft, setDraft] = useState("");
  const [applied, setApplied] = useState("");
  const [removedNotice, setRemovedNotice] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [elementsOpen, setElementsOpen] = useState(false);
  const codeRef = useRef(null);

  const pending = mode === "html" && draft !== applied;
  const current = blocks.length ? Math.min(Math.max(selected ?? 0, 0), blocks.length - 1) : null;
  const block = current === null ? null : blocks[current];

  // A value from outside — the page loading, a save, HTML applied — is read
  // again; one this editor wrote itself is not, so the blocks keep their ids.
  useEffect(() => {
    if (value === lastEmitted.current) return;
    lastEmitted.current = value;
    setBlocks(parseBlocks(value));
  }, [value]);

  useEffect(() => {
    onBusyChange?.(imageBusy || tooLong || pending);
  }, [imageBusy, tooLong, pending, onBusyChange]);
  useEffect(() => () => onBusyChange?.(false), [onBusyChange]);
  useEffect(() => {
    onStatusChange?.({ mode, pending });
  }, [mode, pending, onStatusChange]);

  // The expanded code editor covers the page; the page must not scroll under it.
  useEffect(() => {
    if (!expanded) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (event) => {
      if (event.key === "Escape") setExpanded(false);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener("keydown", onKey);
    };
  }, [expanded]);

  const commit = (next, select) => {
    setBlocks(next);
    if (select !== undefined) onSelect?.(select);
    let serialized;
    try {
      serialized = serializeBlocks(next);
    } catch {
      setTooLong(true);
      return;
    }
    setTooLong(false);
    lastEmitted.current = serialized;
    onChange(serialized);
  };

  const update = (patch) => commit(blocks.map((item, index) => (index === current ? { ...item, ...patch } : item)));

  const move = (step) => {
    const next = [...blocks];
    const [item] = next.splice(current, 1);
    next.splice(current + step, 0, item);
    commit(next, current + step);
  };

  const remove = () => {
    setConfirmRemove(false);
    commit(
      blocks.filter((_, index) => index !== current),
      Math.max(0, current - 1),
    );
  };

  const add = (type) => {
    const at = current === null ? blocks.length : current + 1;
    const next = [...blocks];
    next.splice(at, 0, createBlock(type, t));
    commit(next, at);
  };

  const addStructure = (name) => {
    const at = blocks.length;
    commit([...blocks, ...createStructure(name, t)], at);
  };

  // --- HTML mode ---

  const openHtml = () => {
    const formatted = formatAboutHtml(aboutToHtml(value));
    setDraft(formatted);
    setApplied(formatted);
    setRemovedNotice(false);
    setMode("html");
  };

  const applyHtml = () => {
    const raw = normalizeAboutEmbeds(draft);
    const cleaned = sanitizeAboutHtml(raw);
    const serialized = serializeAboutHtml(cleaned);
    if (serialized.length > ABOUT_MAX_LENGTH) {
      setTooLong(true);
      return;
    }
    setTooLong(false);
    setRemovedNotice(countElements(cleaned) < countElements(raw));
    const formatted = formatAboutHtml(cleaned);
    setDraft(formatted);
    setApplied(formatted);
    lastEmitted.current = serialized;
    setBlocks(parseBlocks(serialized));
    onChange(serialized);
  };

  const snippets = [
    ...BLOCK_TYPES.map((type) => ({
      key: type,
      label: t(`${k}.blocks.${type}`),
      html: blockToHtml(createBlock(type, t)),
    })),
  ];

  const blockItems = BLOCK_TYPES.map((type) => ({
    value: type,
    label: t(`${k}.blocks.${type}`),
    description: t(`${k}.blockHints.${type}`),
    icon: BLOCK_ICONS[type],
  }));

  const structureItems = Object.keys(STRUCTURES).map((name) => ({
    value: name,
    label: t(`${k}.structures.${name}`),
    description: STRUCTURES[name].map((type) => t(`${k}.blocks.${type}`)).join(" · "),
    icon: "bi-layout-text-window-reverse",
  }));

  const adders = (
    <div className="flex flex-wrap gap-2">
      <MenuButton
        primary
        label={t(`${k}.addBlock`)}
        icon="bi-plus-lg"
        items={blockItems}
        onPick={add}
        disabled={disabled}
      />
      <MenuButton
        label={t(`${k}.chooseStructure`)}
        icon="bi-layout-wtf"
        items={structureItems}
        onPick={addStructure}
        disabled={disabled}
      />
    </div>
  );

  return (
    <div className="flex flex-col gap-5">
      <div role="group" aria-label={t(`${k}.modeLabel`)} className="flex flex-wrap gap-2">
        <button
          type="button"
          aria-pressed={mode === "visual"}
          disabled={pending}
          title={pending ? t(`${k}.html.applyFirst`) : undefined}
          onClick={() => setMode("visual")}
          className={modeButtonClass(mode === "visual")}
        >
          <i className="bi bi-pencil" aria-hidden="true" />
          {t(`${k}.visual`)}
        </button>
        <button
          type="button"
          aria-pressed={mode === "html"}
          onClick={() => mode !== "html" && openHtml()}
          className={modeButtonClass(mode === "html")}
        >
          <i className="bi bi-code-slash" aria-hidden="true" />
          {t(`${k}.html.label`)}
        </button>
      </div>

      {mode === "visual" ? (
        blocks.length === 0 ? (
          <div className="flex flex-col items-center gap-4 rounded-xl border-2 border-dashed border-ink-200 px-5 py-8 text-center">
            <i className="bi bi-layout-text-window text-3xl text-ink-400" aria-hidden="true" />
            <p className="m-0 max-w-sm text-sm text-ink-600">{t(`${k}.empty`)}</p>
            {adders}
          </div>
        ) : (
          <>
            {/* The picker gets the whole row: block names are long, and the
                column is narrow beside the preview. */}
            <div>
              <label htmlFor={`${ids}-block`} className="sr-only">
                {t(`${k}.blockPicker`)}
              </label>
              <select
                id={`${ids}-block`}
                value={current}
                onChange={(event) => onSelect?.(Number(event.target.value))}
                className={formControlClass}
              >
                {blocks.map((item, index) => {
                  const title = blockTitle(item);
                  return (
                    <option key={item.id} value={index}>
                      {`${t(`${k}.blockNumber`, { number: index + 1 })} ${t(`${k}.blocks.${item.type}`)}${title ? ` — ${title}` : ""}`}
                    </option>
                  );
                })}
              </select>
            </div>

            <div
              key={block.id}
              className="flex flex-col gap-5 rounded-xl border border-ink-200 p-4"
              role="group"
              aria-label={t(`${k}.blocks.${block.type}`)}
            >
              <div className="-mt-1.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                <p className="m-0 flex items-center gap-2 text-xs font-bold tracking-wide text-ink-500 uppercase">
                  <i className={`bi ${BLOCK_ICONS[block.type]} text-sm text-brand-600`} aria-hidden="true" />
                  {t(`${k}.blocks.${block.type}`)}
                </p>
                <div className="flex flex-wrap items-center gap-0.5">
                  <button
                    type="button"
                    className={toolButtonClass}
                    disabled={disabled || current === 0}
                    onClick={() => move(-1)}
                    aria-label={t(`${k}.moveUp`)}
                    title={t(`${k}.moveUp`)}
                  >
                    <i className="bi bi-arrow-up" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className={toolButtonClass}
                    disabled={disabled || current === blocks.length - 1}
                    onClick={() => move(1)}
                    aria-label={t(`${k}.moveDown`)}
                    title={t(`${k}.moveDown`)}
                  >
                    <i className="bi bi-arrow-down" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className={dangerToolClass}
                    disabled={disabled}
                    onClick={() => setConfirmRemove(true)}
                  >
                    <i className="bi bi-trash3" aria-hidden="true" />
                    {t(`${k}.removeBlock`)}
                  </button>
                </div>
              </div>
              <BlockForm
                block={block}
                update={update}
                disabled={disabled}
                onBusyChange={setImageBusy}
                onEditHtml={openHtml}
              />
            </div>

            {adders}
          </>
        )
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="m-0 text-sm text-ink-500">{t(`${k}.html.caption`)}</p>
            <div className="flex flex-wrap gap-1">
              <button type="button" className={toolButtonClass} onClick={() => setDraft(formatAboutHtml(draft))}>
                <i className="bi bi-text-indent-left" aria-hidden="true" />
                {t(`${k}.html.format`)}
              </button>
              <button type="button" className={toolButtonClass} onClick={() => setExpanded(true)}>
                <i className="bi bi-arrows-fullscreen" aria-hidden="true" />
                {t(`${k}.html.expand`)}
              </button>
            </div>
          </div>

          <div
            className={expanded ? "fixed inset-0 z-1100 flex flex-col gap-3 bg-white p-3 sm:p-6" : "flex flex-col"}
            role={expanded ? "dialog" : undefined}
            aria-modal={expanded ? "true" : undefined}
            aria-label={expanded ? t(`${k}.html.caption`) : undefined}
          >
            {expanded && (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="m-0 text-base font-semibold text-ink-900">{t(`${k}.html.caption`)}</p>
                <button type="button" className={btnSecondaryClass} onClick={() => setExpanded(false)}>
                  <i className="bi bi-fullscreen-exit" aria-hidden="true" />
                  {t(`${k}.html.collapse`)}
                </button>
              </div>
            )}
            <Suspense
              fallback={
                <div className="flex h-[22rem] items-center justify-center rounded-lg border border-ink-200 bg-ink-50 text-sm text-ink-500">
                  {t(`${k}.html.loading`)}
                </div>
              }
            >
              <HtmlCodeEditor
                ref={codeRef}
                value={draft}
                onChange={setDraft}
                label={t(`${k}.html.editorLabel`)}
                expanded={expanded}
              />
            </Suspense>
            {expanded && (
              <div className="flex flex-wrap justify-end gap-2">
                <button type="button" className={btnPrimaryClass} disabled={!pending} onClick={applyHtml}>
                  {t(`${k}.html.apply`)}
                </button>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <p
              role="status"
              className={`m-0 flex items-center gap-1.5 text-sm font-medium ${pending ? "text-amber-700" : "text-brand-700"}`}
            >
              <i className={`bi ${pending ? "bi-exclamation-circle-fill" : "bi-check-circle-fill"}`} aria-hidden="true" />
              {t(pending ? `${k}.html.pending` : `${k}.html.applied`)}
            </p>
            <div className="flex flex-wrap gap-2">
              <button type="button" className={btnPrimaryClass} disabled={!pending} onClick={applyHtml}>
                {t(`${k}.html.apply`)}
              </button>
              <button type="button" className={btnSecondaryClass} disabled={!pending} onClick={() => setDraft(applied)}>
                {t(`${k}.html.discard`)}
              </button>
            </div>
          </div>
          {removedNotice && (
            <p className="m-0 flex items-start gap-1.5 text-xs text-amber-700" role="status">
              <i className="bi bi-shield-exclamation mt-px" aria-hidden="true" />
              {t(`${k}.html.removed`)}
            </p>
          )}
          <p className="m-0 text-xs text-ink-500">{t(`${k}.html.note`)}</p>

          <div className="rounded-xl border border-ink-200">
            <button
              type="button"
              aria-expanded={elementsOpen}
              aria-controls={`${ids}-elements`}
              onClick={() => setElementsOpen((open) => !open)}
              className="flex min-h-11 w-full cursor-pointer items-center gap-2 rounded-xl px-4 text-left text-sm font-semibold text-ink-800 transition hover:bg-ink-50 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-brand-600"
            >
              <i className={`bi bi-chevron-right text-xs transition-transform motion-reduce:transition-none ${elementsOpen ? "rotate-90" : ""}`} aria-hidden="true" />
              {t(`${k}.html.elements`)}
            </button>
            {elementsOpen && (
              <div id={`${ids}-elements`} className="flex flex-col gap-3 border-t border-ink-200 px-4 py-3">
                <p className="m-0 text-xs text-ink-500">{t(`${k}.html.elementsHint`)}</p>
                <div className="flex flex-wrap gap-2">
                  {snippets.map((snippet) => (
                    <button
                      key={snippet.key}
                      type="button"
                      onClick={() => codeRef.current?.insert(`\n${formatAboutHtml(snippet.html)}\n`)}
                      className="inline-flex min-h-11 cursor-pointer items-center gap-1.5 rounded-full border border-ink-200 bg-white px-3 text-xs font-semibold text-ink-700 transition hover:border-brand-600 hover:text-brand-700 focus-visible:outline-2 focus-visible:outline-brand-600"
                    >
                      <i className={`bi ${BLOCK_ICONS[snippet.key]}`} aria-hidden="true" />
                      {snippet.label}
                    </button>
                  ))}
                </div>
                <p className="m-0 text-xs text-ink-500">
                  {t(`${k}.html.classes`)}{" "}
                  <span className="font-mono [overflow-wrap:anywhere]">
                    {ABOUT_CLASSES.filter((name) => !name.startsWith("ep-icon-")).join(" · ")}
                  </span>
                </p>
              </div>
            )}
          </div>
        </>
      )}

      {tooLong && (
        <p className="m-0 text-sm text-red-700" role="alert">
          {t(`${k}.tooLong`)}
        </p>
      )}
      <p className="m-0 text-right text-xs text-ink-400">
        {value.length.toLocaleString()} / {ABOUT_MAX_LENGTH.toLocaleString()}
      </p>

      {confirmRemove && block && (
        <ConfirmDialog
          message={t(`${k}.removeConfirm`, { name: t(`${k}.blocks.${block.type}`) })}
          confirmLabel={t(`${k}.removeBlock`)}
          cancelLabel={t(`${k}.keepBlock`)}
          onConfirm={remove}
          onCancel={() => setConfirmRemove(false)}
        />
      )}
    </div>
  );
};

AboutBlocksEditor.propTypes = {
  /** The stored About value: legacy text, or the marker and rich HTML. */
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  /** True while saving must wait: an upload, too much content, unapplied HTML. */
  onBusyChange: PropTypes.func,
  /** The block being edited (its index); the page holds it, the preview shows it. */
  selected: PropTypes.number,
  onSelect: PropTypes.func,
  /** `{ mode, pending }`: which mode, and whether HTML awaits "Apply". */
  onStatusChange: PropTypes.func,
};

export default AboutBlocksEditor;
